import React, { useState, useRef, useEffect, useCallback } from 'react';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from '../utils/googleMapsLoader';

interface AddressDetails {
  address: string;
  suburb: string;
  council: string;
  postcode: string;
  latitude: number;
  longitude: number;
}

interface AddressAutocompleteProps {
  onAddressSelect: (address: AddressDetails) => void;
  placeholder?: string;
  initialValue?: string;
  className?: string;
  disabled?: boolean;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  placeholder = "Start typing an address...",
  initialValue = "",
  className = "",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Initialize Google Places services
  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Load Google Maps API if not already loaded
        if (!isGoogleMapsLoaded()) {
          await loadGoogleMapsAPI();
        }

        // Initialize services once API is loaded
        if (window.google && window.google.maps && window.google.maps.places) {
          // For now, continue using the AutocompleteService (it's still supported)
          // The new API will be implemented in a future update
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          
          // Create a dummy div for PlacesService (required by Google API)
          const dummyDiv = document.createElement('div');
          placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
      } catch (error) {
        console.error('Failed to initialize Google Maps services:', error);
      }
    };

    initializeServices();
  }, []);

  // Handle input changes with debouncing
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setSelectedIndex(-1);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  }, []);

  // Fetch address predictions from Google Places API
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteService.current) return;

    setIsLoading(true);

    const request = {
      input,
      componentRestrictions: { country: 'au' }, // Restrict to Australia
      types: ['address'], // Only return addresses
    };

    autocompleteService.current.getPlacePredictions(request, (predictions: any, status: any) => {
      setIsLoading(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setPredictions(predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    });
  }, []);

  // Get detailed place information
  const getPlaceDetails = useCallback((placeId: string) => {
    if (!placesService.current) return;

    const request = {
      placeId,
      fields: [
        'formatted_address',
        'address_components',
        'geometry.location'
      ]
    };

    placesService.current.getDetails(request, (place: any, status: any) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const addressDetails = parseAddressComponents(place);
        onAddressSelect(addressDetails);
        setShowDropdown(false);
      }
    });
  }, [onAddressSelect]);

  // Parse Google Places address components into our format
  const parseAddressComponents = (place: any): AddressDetails => {
    const components = place.address_components || [];
    
    let streetNumber = '';
    let streetName = '';
    let suburb = '';
    let council = '';
    let postcode = '';

    components.forEach((component: any) => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('route')) {
        streetName = component.long_name;
      } else if (types.includes('locality') || types.includes('sublocality_level_1')) {
        suburb = component.long_name;
      } else if (types.includes('administrative_area_level_2')) {
        council = component.long_name;
      } else if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
    });

    // Construct full address
    const addressParts = [streetNumber, streetName].filter(Boolean);
    const fullAddress = addressParts.length > 0 
      ? addressParts.join(' ')
      : place.formatted_address || '';

    // Get coordinates
    const location = place.geometry?.location;
    const latitude = location?.lat() || 0;
    const longitude = location?.lng() || 0;

    return {
      address: fullAddress,
      suburb: suburb || 'Unknown',
      council: council || 'Unknown',
      postcode: postcode || '0000',
      latitude,
      longitude
    };
  };

  // Handle prediction selection
  const handlePredictionSelect = useCallback((prediction: PlacePrediction) => {
    setInputValue(prediction.description);
    getPlaceDetails(prediction.place_id);
  }, [getPlaceDetails]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handlePredictionSelect(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, predictions, selectedIndex, handlePredictionSelect]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-urban-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cardboard bg-sky-neutral ${className}`}
          autoComplete="off"
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cardboard"></div>
          </div>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-teddy border border-urban-300 rounded-md shadow-card max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className={`px-4 py-3 cursor-pointer hover:bg-teddy-500 border-b border-urban-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-cardboard-50 border-cardboard-200' : ''
              }`}
            >
              <div className="flex flex-col">
                <span className="font-medium text-urban">
                  {prediction.structured_formatting.main_text}
                </span>
                <span className="text-sm text-urban-300">
                  {prediction.structured_formatting.secondary_text}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && predictions.length === 0 && !isLoading && inputValue.length >= 3 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-teddy border border-urban-300 rounded-md shadow-card"
        >
          <div className="px-4 py-3 text-urban-300 text-sm">
            No addresses found. Try a different search term.
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;